import type { components } from '@trackercop/contracts';
import {
  cookieCategoryPalette,
  payloadCategoryPalette,
  scriptCategoryPalette,
} from '@/lib/category-palette';
import { cn } from '@/lib/utils';

type ScriptCategory = components['schemas']['ScriptCategory'];
type CookieCategory = components['schemas']['CookieCategory'];
type PayloadCategory = components['schemas']['PayloadCategory'];

interface PaletteEntry {
  bg: string;
  text: string;
  label: string;
}

/**
 * Look up a palette entry, falling back to the palette's `unknown` slot
 * when the row's stored category isn't in the spec's enum. Keeps the
 * unknown-string visible as the chip label so the gap is diagnosable
 * from the UI rather than silently hidden behind "Unknown".
 *
 * Without this fallback the chip would access `palette.bg` on
 * `undefined` and crash the entire row, which surfaced as a blank
 * cookies tab on staging when a row carried an off-spec category.
 */
function resolvePalette<TCategory extends string>(
  palette: Record<string, PaletteEntry>,
  category: TCategory
): PaletteEntry {
  const direct = palette[category];
  if (direct) return direct;
  const fallback = palette.unknown ?? {
    bg: 'bg-neutral-400/15',
    text: 'text-neutral-700',
    label: 'Unknown',
  };
  return { ...fallback, label: category || fallback.label };
}

interface ScriptCategoryChipProps {
  category: ScriptCategory;
  cmpProvider?: string | null;
}

export function ScriptCategoryChip({ category, cmpProvider }: ScriptCategoryChipProps) {
  const palette = resolvePalette(scriptCategoryPalette, category);
  const label = category === 'consent-management' && cmpProvider
    ? `${palette.label} (${cmpProvider})`
    : palette.label;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        palette.bg,
        palette.text,
      )}
      data-testid={`script-category-chip-${category}`}
    >
      {label}
    </span>
  );
}

export function CookieCategoryChip({ category }: { category: CookieCategory }) {
  const palette = resolvePalette(cookieCategoryPalette, category);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        palette.bg,
        palette.text,
      )}
    >
      {palette.label}
    </span>
  );
}

export function PayloadCategoryChip({ category }: { category: PayloadCategory }) {
  const palette = resolvePalette(payloadCategoryPalette, category);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        palette.bg,
        palette.text,
      )}
      data-testid={`payload-category-chip-${category}`}
    >
      {palette.label}
    </span>
  );
}
